/**
 * What the Adjust editor's journal opens on — pure, so the seam that used to
 * hide a defect is now four unit tests (spec §2.3).
 *
 * The editor is opened more than once, and each time it has to show the SAME
 * two things in the right order: the wizard's step choices underneath, the
 * operator's own fine-tuning on top. Three rules do it:
 *
 *  1. **Entry 0 is rebuilt from the steps, every time.** It carries
 *     `designPatch` — which, since `adjustLift` was split out of it, is the
 *     steps' own writes and nothing else. A step changed since the last visit
 *     therefore lands automatically; there is no stale copy to go looking for.
 *  2. **Entries 1..n are replayed from the saved journal**, so Undo still pops
 *     ONE real change instead of the whole session.
 *  3. **…pruned to the lift the wizard still holds.** A step that answered a
 *     key the operator had fine-tuned drops that key from `adjustLift`
 *     (`state.ts`), and the replay must not resurrect it from the journal —
 *     that is what makes a later step click outrank an earlier fine-tune.
 *
 * `brand` is never replayed: the host folds the panel's colour and face rows
 * into the wizard's own `colors` / `fonts`, which is where the preview reads
 * them from, so replaying them could only ever re-apply a value that has since
 * been answered somewhere else.
 */
import {
  emptyEditState,
  parseEditState,
  type ClipEditState,
  type EditJournalEntry,
  type EditPatch,
} from "../../../../src/lib/clipEditState.ts";
import { SEED_ENTRY_LABEL } from "./keys";

/** What a design draft holds. The bare `ClipEditState` earlier builds wrote is
 *  still readable — a wizard in flight must not lose its journal to a deploy. */
export interface DesignDraft {
  state: ClipEditState;
}

/** The saved journal, or null for "nothing usable there". */
export function parseDesignDraft(raw: string | null | undefined): ClipEditState | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  const env = parsed as { state?: unknown } | null;
  if (env && typeof env === "object" && env.state) return parseEditState(JSON.stringify(env.state));
  return parseEditState(raw);
}

/** True when this entry is the wizard's own seed rather than the operator's. */
export function isSeedEntry(e: EditJournalEntry | undefined): boolean {
  return e?.label === SEED_ENTRY_LABEL;
}

/** Layout paths a patch touches, as `section.key` (`overlays` as itself). */
function pathsOf(layout: NonNullable<EditPatch["layout"]> | undefined): Set<string> {
  const out = new Set<string>();
  for (const [section, value] of Object.entries(layout ?? {})) {
    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      for (const key of Object.keys(value)) out.add(`${section}.${key}`);
    } else out.add(section);
  }
  return out;
}

/**
 * One journal entry, reduced to the layout paths the lift still owns. Returns
 * null when nothing survives — a colour-only entry, or a key a later step
 * answered — so the journal never carries an edit that changes nothing.
 */
export function pruneEntry(
  entry: EditJournalEntry,
  keep: ReadonlySet<string>,
): EditJournalEntry | null {
  const layout = entry.patch.layout;
  if (!layout) return null;
  const out: Record<string, unknown> = {};
  for (const [section, value] of Object.entries(layout)) {
    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      const kept = Object.fromEntries(Object.entries(value as object).filter(([k]) => keep.has(`${section}.${k}`)));
      if (Object.keys(kept).length > 0) out[section] = kept;
    } else if (keep.has(section)) {
      out[section] = value;
    }
  }
  if (Object.keys(out).length === 0) return null;
  return { ...entry, patch: { layout: out as EditPatch["layout"] } };
}

/**
 * The journal this mount opens on.
 *
 * `steps` is the wizard's `designPatch` (entry 0); `lift` is its `adjustLift`
 * (what the operator's replayed entries may still say). With no saved journal
 * but a lift in hand — a cleared draft, a different browser — the lift is
 * replayed as ONE entry, so the work is visible and undoable instead of silently
 * baked into the seed.
 */
export function openingJournal(
  saved: ClipEditState | null,
  steps: EditPatch | undefined,
  lift: EditPatch | undefined,
): ClipEditState {
  const seeded = steps && Object.keys(steps).length > 0;
  const journal: EditJournalEntry[] = seeded
    ? [{ ts: 0, source: "inspector", label: SEED_ENTRY_LABEL, patch: steps! }]
    : [];

  // NO steps and NO lift is the CLASSIC host (`Onboard.tsx`), whose journal is
  // the whole design rather than a layer over a wizard's choices. Its draft is
  // handed straight back: rebuilding it from a wizard state it does not have
  // would discard the Design step's work on every remount (spec invariant 4).
  if (journal.length === 0 && !lift) return saved ?? emptyEditState();

  const keep = pathsOf(lift?.layout);
  if (keep.size === 0) return journal.length > 0 ? { version: 1, journal } : emptyEditState();

  const mine = (saved?.journal ?? []).filter((e) => !isSeedEntry(e));
  const pruned = mine.map((e) => pruneEntry(e, keep)).filter((e): e is EditJournalEntry => e !== null);
  if (pruned.length === 0) {
    journal.push({ ts: 0, source: "inspector", label: "Your fine-tuning", patch: lift! });
  } else {
    journal.push(...pruned);
  }
  return { version: 1, journal };
}

/** How many of the journal's entries are the operator's — what Review counts
 *  as "changes on the video". */
export function fineTuneCount(state: ClipEditState): number {
  return state.journal.filter((e) => !isSeedEntry(e)).length;
}
